import { AnnotationType } from '@tabeeb/contracts/main-api/swagger-main-api'
import type { KonvaEventObject } from 'konva/lib/Node'

import LineContainer from '../containers/LineContainer'
import TextContainer from '../containers/TextContainer'
import StrokeContainer from '../containers/StrokeContainer'
import DistanceContainer from '../containers/DistanceContainer'
import PolygonContainer from '../containers/PolygonContainer'
import CircleContainer from '../containers/CircleContainer'
import HotspotContainer from '../containers/HotspotContainer'
import AIHotspotContainer from '../containers/AIHotspotContainer'
import ArrowLineContainer from '../containers/ArrowLineContainer'
import RectangleContainer from '../containers/RectangleContainer'
import LinkedPageContainer from '../containers/LinkedPageContainer'

import Line from '../components/Line'
import Text from '../components/Text'
import Stroke from '../components/Stroke'
import Distance from '../components/Distance'
import Polygon from '../components/Polygon'
import Circle from '../components/Circle'
import Hotspot from '../components/Hotspot'
import AIHotspot from '../components/AIHotspot'
import ArrowLine from '../components/ArrowLine'
import Rectangle from '../components/Rectangle'
import LinkedPage from '../components/LinkedPage'

export const getIsAnnotationSupportsTransformation = (annotationType: AnnotationType): boolean => {
  if (
    annotationType === AnnotationType.StrokeDrawShapeRectangle ||
    annotationType === AnnotationType.StrokeDrawShapeRectangleFilled ||
    annotationType === AnnotationType.StrokeDrawShapeEllipse ||
    annotationType === AnnotationType.StrokeDrawShapeEllipseFilled
  ) {
    return true
  }

  return false
}

export const getAnnotationTypeName = (annotationType: AnnotationType) => {
  switch (annotationType) {
    case AnnotationType.Text:
      return 'Text'
    case AnnotationType.StrokeDraw:
      return 'Stroke'
    case AnnotationType.Distance:
      return 'Distance'
    case AnnotationType.Polygon:
      return 'Polygon'
    case AnnotationType.StrokeDrawShapeLine:
      return 'Line'
    case AnnotationType.HotSpot:
      return 'Hotspot'
    case AnnotationType.AIHotspot:
      return 'AI Hotspot'
    case AnnotationType.LinkedPage:
      return 'Linked page'
    case AnnotationType.StrokeDrawShapeLineArrowEnd:
    case AnnotationType.StrokeDrawShapeLineArrowBoth:
      return 'Arrow'
    case AnnotationType.StrokeDrawShapeRectangle:
    case AnnotationType.StrokeDrawShapeRectangleFilled:
      return 'Rectangle'
    case AnnotationType.StrokeDrawShapeEllipse:
    case AnnotationType.StrokeDrawShapeEllipseFilled:
      return 'Circle'
    default:
      return null
  }
}

export const getAnnotationComponentByType = (annotationType: AnnotationType) => {
  switch (annotationType) {
    case AnnotationType.Distance:
      return Distance
    case AnnotationType.Text:
      return Text
    case AnnotationType.StrokeDraw:
      return Stroke
    case AnnotationType.Polygon:
      return Polygon
    case AnnotationType.StrokeDrawShapeLine:
      return Line
    case AnnotationType.HotSpot:
      return Hotspot
    case AnnotationType.AIHotspot:
      return AIHotspot
    case AnnotationType.LinkedPage:
      return LinkedPage
    case AnnotationType.StrokeDrawShapeLineArrowEnd:
    case AnnotationType.StrokeDrawShapeLineArrowBoth:
      return ArrowLine
    case AnnotationType.StrokeDrawShapeRectangle:
    case AnnotationType.StrokeDrawShapeRectangleFilled:
      return Rectangle
    case AnnotationType.StrokeDrawShapeEllipse:
    case AnnotationType.StrokeDrawShapeEllipseFilled:
      return Circle
    default:
      return null
  }
}

export const getStageOffset = (eventTarget: KonvaEventObject<MouseEvent>['target']): { X: number; Y: number } => {
  const stage = eventTarget.getStage()
  if (stage === null) {
    return { X: 0, Y: 0 }
  }

  const stagePosition = stage.getPosition()

  const { scaleX } = stage.attrs
  const { scaleY } = stage.attrs

  return {
    X: stagePosition.x / scaleX,
    Y: stagePosition.y / scaleY,
  }
}

export const getPointerPosition = (eventTarget: KonvaEventObject<MouseEvent>['target']): { X: number; Y: number } => {
  const stage = eventTarget.getStage()
  if (stage === null) {
    return { X: 0, Y: 0 }
  }

  const stagePosition = stage.getPosition()
  const pointerPosition = stage.getPointerPosition() ?? { x: 0, y: 0 }

  const { scaleX } = stage.attrs
  const { scaleY } = stage.attrs

  return {
    X: -stagePosition.x / scaleX + pointerPosition.x / scaleX,
    Y: -stagePosition.y / scaleY + pointerPosition.y / scaleY,
  }
}

export const convertAnnotationObjectToComponent = (annotation, props = {}): JSX.Element | null => {
  if (!annotation) return null

  switch (annotation.Type) {
    case AnnotationType.Distance:
      return <DistanceContainer key={annotation.Id} Id={annotation.Id} {...props} />
    case AnnotationType.Text:
      return <TextContainer key={annotation.Id} Id={annotation.Id} {...props} />
    case AnnotationType.StrokeDraw:
      return <StrokeContainer key={annotation.Id} Id={annotation.Id} {...props} />
    case AnnotationType.Polygon:
      return <PolygonContainer key={annotation.Id} Id={annotation.Id} {...props} />
    case AnnotationType.StrokeDrawShapeLine:
      return <LineContainer key={annotation.Id} Id={annotation.Id} {...props} />
    case AnnotationType.HotSpot:
      return <HotspotContainer key={annotation.Id} Id={annotation.Id} {...props} />
    case AnnotationType.AIHotspot:
      return <AIHotspotContainer key={annotation.Id} Id={annotation.Id} {...props} />
    case AnnotationType.LinkedPage:
      return <LinkedPageContainer key={annotation.Id} Id={annotation.Id} {...props} />
    case AnnotationType.StrokeDrawShapeLineArrowEnd:
    case AnnotationType.StrokeDrawShapeLineArrowBoth:
      return <ArrowLineContainer key={annotation.Id} Id={annotation.Id} {...props} />
    case AnnotationType.StrokeDrawShapeRectangle:
    case AnnotationType.StrokeDrawShapeRectangleFilled:
      return <RectangleContainer key={annotation.Id} Id={annotation.Id} {...props} />
    case AnnotationType.StrokeDrawShapeEllipse:
    case AnnotationType.StrokeDrawShapeEllipseFilled:
      return <CircleContainer key={annotation.Id} Id={annotation.Id} {...props} />
    default:
      return null
  }
}
